import * as React from 'react';
import { useIntl } from 'react-intl';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ACTIONS_KEY,
  CUSTOMER_KEY,
  LOCKED_KEY,
  TIMESTAMP_KEY,
  VEHICLE_KEY,
  ESCAPE_KEY,
} from '../constants';
import useKeyPress from '../../../hooks/useKeyPress';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { DEFAULT_ORDER_COLUMNS, ITableSettingsData, ITripsOverviewColumn } from '../interfaces';
import { customersAtom } from '../../../store/recoil/customers';
import {
  columnsAtom,
  columnsKeyOrderAtom,
  isTableSettingsMenuOpenedAtom,
  sortFilterAtom,
  tableSettingsStateAtom,
} from '../TripsOverviewState';
import { arrayOrder, saveTableSettingAndSummary } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';
import { isDemo } from '../../../Services/DemoService';

export default function TableSettings(): JSX.Element {
  const [columnsTable, setColumnsTable] = useRecoilState<ITripsOverviewColumn[]>(columnsAtom);
  const [filteredColumns, setFilteredColumns] = React.useState<ITripsOverviewColumn[]>([]);
  const setOrderForColumns = useSetRecoilState(columnsKeyOrderAtom);
  const { formatMessage: f } = useIntl();
  const [isTableSettingsMenuOpened, setIsTableSettingsMenuOpened] = useRecoilState(
    isTableSettingsMenuOpenedAtom
  );
  const customer = useRecoilValue(customersAtom);
  const [isResetBtnDisable, setIsResetBtnDisable] = React.useState<boolean>(true);
  const [scrollPosition, setScrollPosition] = React.useState(0);

  const [prevColumns, setPrevColumns] = React.useState<ITripsOverviewColumn[]>([]);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(sortFilterAtom);
  const tableSettingsState = useRecoilValue<ITableSettingsData | null>(tableSettingsStateAtom);
  const { trackEvent } = useAnalytics();
  const [initialColumnsState, setInitialColumnsState] = React.useState<ITripsOverviewColumn[]>([]);

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  const handleCloseSettings = () => {
    setIsTableSettingsMenuOpened(false);
    setFilteredColumns(JSON.parse(JSON.stringify(initialColumnsState)));
  };

  useOutsideClick(clickOutsideRef, (_event) => {
    // event.preventDefault();

    handleCloseSettings();
  });

  useKeyPress(ESCAPE_KEY, () => {
    handleCloseSettings();
  });

  React.useEffect(() => {
    const element = document.getElementById('tableSettingsSortableContainer');
    if (element) {
      element.scrollTo(0, scrollPosition);
    }
  });

  React.useEffect(() => {
    setFilteredColumns(columnsTable ? columnsTable.filter((i) => i.key !== ACTIONS_KEY) : []);
  }, [columnsTable]);

  const handleDefaultColumns = (columns: ITripsOverviewColumn[]) => {
    if (
      columns[0].key !== LOCKED_KEY ||
      columns[1].key !== TIMESTAMP_KEY ||
      columns[2].key !== VEHICLE_KEY
    ) {
      const sortedArray = [
        ...columns.filter((item) => item.key === LOCKED_KEY),
        ...columns.filter((item) => item.key === TIMESTAMP_KEY),
        ...columns.filter((item) => item.key === VEHICLE_KEY),
        ...columns.filter((item) => !item.default && item.key !== ACTIONS_KEY),
        ...columns.filter((item) => item.key === ACTIONS_KEY),
      ];

      setFilteredColumns(sortedArray ? sortedArray.filter((i) => i.key !== ACTIONS_KEY) : []);
    } else {
      setFilteredColumns(columns ? columns.filter((i) => i.key !== ACTIONS_KEY) : []);
    }
  };

  const arrayMoveMutable = (array: ITripsOverviewColumn[], fromIndex: number, toIndex: number) => {
    const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

    if (startIndex >= 0 && startIndex < array.length) {
      const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

      const [item] = array.splice(fromIndex, 1);
      array.splice(endIndex, 0, item);
    }
  };

  const handleSetOrderColumns = (newOrderColumns: { name: string; hidden: boolean }[]) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ITripsOverviewColumn[] = [];

    columnsTable.map((item) => {
      const findHidden = newOrderColumns.find((findItem) => item.key === findItem.name);

      if (findHidden) {
        tempTableSettingOrder.push({
          key: item.key,
          default: item.default,
          hidden: findHidden.hidden,
        });
      } else if (customer.length === 1 && item.key === CUSTOMER_KEY) {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: true });
      } else {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: item.hidden });
      }
    });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const handleTableSettings = async (data: any) => {
    setIsTableSettingsMenuOpened(false);
    const tempData: { name: string; hidden: boolean }[] = [];

    data.map((item) => {
      tempData.push({ name: item.key, hidden: item.hidden });
    });

    if (tableSettingsState) {
      const dataForRequest = {
        ...tableSettingsState,
        columns: [...tempData],
        sort: { column: TIMESTAMP_KEY, direction: sortFilter.timestamp },
      };

      handleSetOrderColumns(dataForRequest.columns);

      if (isDemo()) {
        return;
      }

      const saveSettings = await saveTableSettingAndSummary(dataForRequest);

      if (saveSettings) {
        const settings = saveSettings.value;
        localStorage.setItem('tableSettings', JSON.stringify(settings));
      }
    }
  };

  const handleApply = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setScrollPosition(0);
    trackEvent(['ride-book', 'click', 'Ride book apply table settings']);

    const order: { key: string; hidden: boolean }[] = [];

    filteredColumns.map((item) => {
      order.push({ key: item.key, hidden: item.hidden });
    });
    order.push({ key: ACTIONS_KEY, hidden: false });

    setOrderForColumns(order);
    handleTableSettings(filteredColumns);
  };

  const handleReset = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const tempTableSettingOrder: ITripsOverviewColumn[] = [];
    trackEvent(['ride-book', 'click', 'Ride book reset table settings']);

    columnsTable.map((item) => {
      const findHidden = DEFAULT_ORDER_COLUMNS.find((findItem) => item.key === findItem.key);

      if (findHidden) {
        tempTableSettingOrder.push({
          key: item.key,
          default: item.default,
          hidden: findHidden.hidden,
        });
      } else if (customer.length === 1 && item.key === CUSTOMER_KEY) {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: true });
      } else {
        tempTableSettingOrder.push({ key: item.key, default: item.default, hidden: item.hidden });
      }
    });

    setOrderForColumns(columnsTable);
    handleDefaultColumns(columnsTable);
  };

  React.useEffect(() => {
    if (JSON.stringify(filteredColumns) !== JSON.stringify(prevColumns)) {
      if (prevColumns.length > 0) {
        setIsResetBtnDisable(false);
      }
      setPrevColumns(filteredColumns);
    }
  }, [filteredColumns, prevColumns]);

  const Element = SortableElement((data: { item: ITripsOverviewColumn }) => {
    return (
      <li
        style={{ zIndex: 1000, cursor: 'grabbing', listStyleType: 'none', padding: '8px 12px' }}
        key={data.item.key}
        data-cy={`ride-book-table-settings-drag-${data.item.key}`}
      >
        <i style={{ margin: '0 2px 0 -4px' }} className="cmd-icons cmd-icon-reorder" />
        <div className="e-checkbox">
          <label>
            <input
              type="checkbox"
              defaultChecked={!data.item.hidden}
              data-cy={`ride-book-table-settings-${data.item.key}`}
            />
            <span style={{ fontSize: '13px' }}>
              {f({ id: `tables.tripsOverview.column.${data.item.key}` })}
            </span>
          </label>
        </div>
      </li>
    );
  });

  const List = SortableContainer(() => {
    return (
      <ul
        className="b-dropdown__list"
        style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}
        id="tableSettingsSortableContainer"
      >
        {filteredColumns.map((i, index) => {
          if (customer.length === 1 && i.key === CUSTOMER_KEY) {
            return null;
          }
          if (i.key === LOCKED_KEY || i.key === TIMESTAMP_KEY || i.key === VEHICLE_KEY) {
            return (
              <li key={`${i.key}-${index}`}>
                <i
                  className="cmd-icons cmd-icon-reorder"
                  style={{
                    color: '#ccc',
                  }}
                />
                <div className="e-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      defaultChecked={i.default}
                      disabled={true}
                      data-cy={`ride-book-table-settings-${i.key}`}
                    />
                    <span>{f({ id: `tables.tripsOverview.column.${i.key}` })}</span>
                  </label>
                </div>
              </li>
            );
          }
          return <Element item={i} index={index} key={`${i.key}-${index}`} />;
        })}
      </ul>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }: any, _event: any) => {
    const element = document.getElementById('tableSettingsSortableContainer');
    const currentTopPosition = element?.scrollTop;
    setScrollPosition(currentTopPosition || 0);
    let columns = JSON.parse(JSON.stringify(filteredColumns));
    const column = columns[oldIndex];
    if (newIndex === oldIndex) {
      const columnName = column.key;
      const columnChecked = column.hidden === true;
      columns = columns.map((i) => {
        if (i.key === columnName) {
          i.hidden = !columnChecked;
        }
        return i;
      });
    }

    arrayMoveMutable(columns, oldIndex, newIndex);

    setFilteredColumns(columns);
  };

  const handleTableSettingsMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (!isTableSettingsMenuOpened) {
      setInitialColumnsState(JSON.parse(JSON.stringify(filteredColumns)));
    }
    setIsTableSettingsMenuOpened(!isTableSettingsMenuOpened);
    trackEvent(['ride book', 'click', 'Open table settings']);
  };

  return (
    <div ref={clickOutsideRef} className="col-12 col-md-auto mr-md-2 mb-2">
      <div
        className={`b-dropdown b-dropdown--switcher ${isTableSettingsMenuOpened ? 'open' : ''} `}
        style={{ width: '220px' }}
      >
        <div
          onClick={handleTableSettingsMenu}
          className="form-control-labels"
          data-cy="ride-book-open-table-settings"
        >
          <i className="cmd-icon cmd-icon-cog fs-4 text-gray-lighten" />
          <span
            className="ml-2"
            title={f({ id: 'tables.tripsOverview.column.tableSettings.tooltip' })}
          >
            {f({ id: 'tables.tripsOverview.column.tableSettings' })}
          </span>
        </div>
        {isTableSettingsMenuOpened && (
          <div id="tableSettings" className="col-auto mr-3">
            <div className="b-dropdown open" style={{ width: '220px', marginLeft: '-14px' }}>
              <div className="b-dropdown__content" style={{ position: 'absolute', zIndex: 999 }}>
                <div className="b-dropdown__title b-dropdown__title--border-0">
                  {f({ id: 'tables.tripsOverview.tableSettings.header' })}
                </div>
                <List onSortEnd={onSortEnd} />
                <div className="b-dropdown__footer b-dropdown__footer--border-on">
                  <a
                    onClick={handleReset}
                    href="#"
                    title={f({ id: 'rides.reset.tooltip' })}
                    className={`${
                      isResetBtnDisable ? 'e-button e-button--gray e-button--disabled' : 'e-button'
                    }`}
                    data-cy="ride-book-table-settings-reset"
                  >
                    Reset
                  </a>
                  <a
                    onClick={handleApply}
                    href="#"
                    title={f({ id: 'rides.apply.tooltip' })}
                    className="e-button e-button--gray"
                    data-cy="ride-book-table-settings-apply"
                  >
                    {f({ id: 'table.apply' })}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
